import type { SortDescriptor } from "react-aria-components";
import { useCallback, useMemo, useState } from "react";

type UseTableSortOptions<T> = {
  initialSortDescriptor?: SortDescriptor;
  // maps column id → actual row key when they differ (e.g. display value ≠ sortable value)
  keyMap?: Partial<Record<string, keyof T>>;
};

export function useTableSort<T extends Record<string, unknown>>(
  rows: T[],
  options?: UseTableSortOptions<T>,
) {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor | undefined>(
    options?.initialSortDescriptor,
  );

  const onSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  }, []);

  const sortedRows = useMemo(() => {
    if (!sortDescriptor?.column) return rows;

    const col = sortDescriptor.column as string;
    const key = (options?.keyMap?.[col] ?? col) as keyof T;

    return [...rows].sort((a, b) => {
      const aVal = a[key] ?? "";
      const bVal = b[key] ?? "";

      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;

      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [rows, sortDescriptor, options?.keyMap]);

  return { onSortChange, sortDescriptor, sortedRows };
}
